<template>
  <div class="view-container">
    <div class="main-layout">
      <!-- ==================== 左侧：报表目录+报表树 ==================== -->
      <div class="left-panel">
        <div class="panel-header"><h4>报表目录</h4></div>
        <div class="panel-body">
          <table class="tree-table">
            <thead><tr><th>目录名称</th></tr></thead>
            <tbody>
              <template v-for="node in flatTree" :key="node._key">
                <tr
                  class="tree-row"
                  :class="{
                    selected: selectedNodeKey === node._key,
                    'is-report': node._isReport,
                    'sub-row': node._depth > 0
                  }"
                  @click="selectTreeNode(node)"
                >
                  <td>
                    <span class="tree-name-cell" :style="{ paddingLeft: (node._depth * 16 + 4) + 'px' }">
                      <!-- 展开/折叠图标（仅目录有子节点时显示） -->
                      <span
                        v-if="!node._isReport && node.children && node.children.length > 0"
                        class="expand-icon"
                        :class="{ expanded: expandedFolderIds.includes(node.id) }"
                        @click.stop="toggleExpandFolder(node)"
                      ><i class="fas fa-caret-right"></i></span>
                      <!-- report 占位 -->
                      <span v-else-if="!node._isReport && !(node.children && node.children.length > 0)" class="expand-placeholder"></span>
                      <span v-else class="expand-placeholder"></span>

                      <!-- 图标 -->
                      <i v-if="node._isReport" class="fas fa-chart-bar" style="color:#6366f1"></i>
                      <i v-else class="fas" :class="(node.children && node.children.length > 0) ? 'fa-folder' : 'fa-folder-open'"></i>

                      <span class="tree-name">{{ node.name }}</span>
                      <span class="count-badge" v-if="node._reportCount">{{ node._reportCount }}</span>
                    </span>
                  </td>
                </tr>
              </template>
              <tr v-if="flatTree.length === 0"><td class="empty-cell">暂无目录</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ==================== 右侧：权限列表 ==================== -->
      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedNodeKey">
            <div class="search-panel">
              <div class="search-row">
                <div class="search-item">
                  <label><i class="fas fa-chart-bar"></i> 报表名称</label>
                  <input v-model="searchForm.reportName" type="text" placeholder="搜索报表" class="search-input">
                </div>
                <div class="search-item">
                  <label><i class="fas fa-tag"></i> 授权类型</label>
                  <select v-model="searchForm.targetType" class="search-select">
                    <option value="">全部</option>
                    <option value="user">用户</option>
                    <option value="org">组织</option>
                    <option value="role">角色</option>
                  </select>
                </div>
                <div class="search-actions">
                  <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
                  <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
                  <button @click="openAddDialog" class="btn btn-success search-btn"><i class="fas fa-plus"></i> 新增权限</button>
                </div>
              </div>
            </div>

            <div class="table-container-inner">
              <table class="data-table">
                <thead><tr>
                  <th width="50">ID</th><th>报表名称</th><th>图表类型</th><th width="80">授权类型</th><th>授权对象</th>
                  <th width="100">权限</th><th width="70">状态</th><th width="120">操作</th>
                </tr></thead>
                <tbody>
                  <tr v-for="p in paginatedData" :key="p.id">
                    <td>{{ p.id }}</td><td>{{ p.reportName }}</td>
                    <td><span class="chart-type-badge">{{ p.chartType }}</span></td>
                    <td><span class="type-badge" :class="p.targetType">{{ typeLabels[p.targetType] }}</span></td>
                    <td>{{ p.targetName }}</td>
                    <td><span class="scope-badge" :class="p.scope">{{ p.scope === 'manage' ? '可管理' : '可查看' }}</span></td>
                    <td><span class="status-badge" :class="p.status==='启用'?'enabled':'disabled'">{{ p.status }}</span></td>
                    <td class="action-cell">
                      <button @click="editPermission(p)" class="btn btn-primary btn-xs"><i class="fas fa-edit"></i></button>
                      <button @click="toggleStatus(p)" class="btn btn-xs" :class="p.status==='启用'?'btn-warning':'btn-success'"><i class="fas" :class="p.status==='启用'?'fa-ban':'fa-check'"></i></button>
                      <button @click="deletePermission(p)" class="btn btn-danger btn-xs"><i class="fas fa-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="paginatedData.length===0"><td colspan="8" class="empty-cell">该{{ selectedNodeIsReport ? '报表' : '目录' }}暂无权限数据</td></tr>
                </tbody>
              </table>
            </div>

            <div class="pagination-row">
              <div class="pagination-info">共 {{ totalCount }} 条记录</div>
              <div class="pagination-controls">
                <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control"><option :value="10">10</option><option :value="20">20</option><option :value="50">50</option></select><span>条</span></div>
                <button @click="prevPage" :disabled="currentPage===1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
                <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn',{active:currentPage===page,disabled:page==='...'}]" :disabled="page==='...'">{{ page }}</button></div>
                <button @click="nextPage" :disabled="currentPage>=totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state"><i class="fas fa-chart-bar"></i><p>请选择一个报表目录</p><p class="hint">左侧目录树点击即可查看对应报表权限</p></div>
        </div>
      </div>
    </div>

    <!-- ==================== 新增/编辑弹窗 ==================== -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-container">
        <div class="modal-header"><h3>{{ editingId ? '编辑权限' : '新增权限' }}</h3><button @click="closeFormModal" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row"><div class="form-group"><label>报表 *</label><div class="search-select"><input v-model="form.reportName" type="text" class="form-control" readonly @click="showReportPicker=true"><button @click="showReportPicker=true" class="search-btn"><i class="fas fa-search"></i></button></div></div></div>
          <div class="form-row">
            <div class="form-group"><label>授权类型 *</label><select v-model="form.targetType" class="form-control"><option value="user">用户</option><option value="org">组织</option><option value="role">角色</option></select></div>
            <div class="form-group"><label>授权对象 *</label><div class="search-select"><input v-model="form.targetName" class="form-control" readonly @click="openTargetPicker"><button @click="openTargetPicker" class="search-btn"><i class="fas fa-search"></i></button></div></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>权限范围</label><select v-model="form.scope" class="form-control"><option value="view">可查看</option><option value="manage">可管理</option></select></div>
            <div class="form-group"><label>状态</label><select v-model="form.status" class="form-control"><option value="启用">启用</option><option value="禁用">禁用</option></select></div>
          </div>
          <div class="form-group"><label>备注</label><textarea v-model="form.remark" class="form-control" rows="2"></textarea></div>
        </div>
        <div class="modal-footer"><button @click="closeFormModal" class="btn btn-default">取消</button><button @click="saveForm" class="btn btn-primary">保存</button></div>
      </div>
    </div>

    <!-- ==================== 报表选择器 ==================== -->
    <div v-if="showReportPicker" class="modal-overlay" @click.self="showReportPicker=false">
      <div class="modal-container picker-modal">
        <div class="modal-header"><h3>选择报表</h3><button @click="showReportPicker=false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="search-box"><input v-model="reportSearch" class="form-control" placeholder="搜索报表"></div>
          <div class="picker-list">
            <div v-for="r in filteredReports" :key="r.id" class="picker-item" @click="selectReport(r)" :class="{ selected: form.reportId === r.id }">
              <i class="fas fa-chart-bar"></i><span>{{ r.name }}</span><span class="picker-info">{{ r.folderName }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 授权对象选择器 ==================== -->
    <div v-if="showTargetPicker" class="modal-overlay" @click.self="showTargetPicker=false">
      <div class="modal-container picker-modal">
        <div class="modal-header"><h3>选择{{ targetTypeLabel }}</h3><button @click="showTargetPicker=false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="search-box">
            <div class="search-input-wrap">
              <i class="fas fa-search search-icon"></i>
              <input v-model="targetSearch" class="search-box-input" :placeholder="'搜索' + targetTypeLabel">
            </div>
          </div>
          <div class="picker-list">
            <template v-if="form.targetType === 'user'">
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item" @click="selectTarget(item)" :class="{ selected: form.targetId === item.id }">
                <i class="fas fa-user"></i><span>{{ item.name }}</span>
              </div>
            </template>
            <template v-else>
              <div v-for="item in filteredTargets" :key="item.id" class="picker-item tree-item" :class="['tree-level-' + (item.level || 0), { selected: form.targetId === item.id }]" @click="selectTarget(item)">
                <i class="fas" :class="form.targetType==='org'?'fa-building':'fa-user-tag'"></i>
                <span>{{ item.name }}</span>
                <span v-if="item.hasChildren" class="tree-hint">···</span>
              </div>
            </template>
            <div v-if="filteredTargets.length === 0" class="picker-empty">无匹配结果</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getReportPermissions, saveReportPermission, deleteReportPermission } from '../api/permission'
import { getCatalogs, getReports, getAllReports } from '../api/report'
import { getOrgTree } from '../api/org'
import { getRoleTree } from '../api/role'
import { getUserList } from '../api/user'

const typeLabels = { user: '用户', org: '组织', role: '角色' }
const currentPage = ref(1); const pageSize = ref(10)
const selectedNodeKey = ref(null)
const expandedFolderIds = ref([])
const showFormModal = ref(false); const showReportPicker = ref(false); const showTargetPicker = ref(false)
const editingId = ref(null); const reportSearch = ref(''); const targetSearch = ref('')

const searchForm = reactive({ reportName: '', targetType: '' })
const form = reactive({ id: null, guid: null, reportId: null, reportName: '', folderId: null, targetType: 'user', targetId: null, targetName: '', scope: 'view', status: '启用', remark: '' })

const reportFolderTree = ref([])
const reportChildMap = ref({})    // catalogId → [report nodes]
const permissions = ref([])
const users = ref([])
const orgs = ref([])
const rls = ref([])
const orgTree = ref([])
const roleTree = ref([])

// 缓存所有报表（用于报表选择器）
const allReportsCache = ref([])

/* ---------- AlloyPermission bit 映射 ---------- */
const alloyToScope = (alloy) => {
  if (!alloy) return 'view'
  return alloy.length >= 2 && alloy[1] === '1' ? 'manage' : 'view'
}
const buildAlloy = (scope, status) => {
  const statusBit = status === '启用' ? '1' : '0'
  const scopeBit = scope === 'manage' ? '1' : '0'
  return statusBit + scopeBit
}

/* ---------- 目录树构建 ---------- */
const buildCatalogTree = (flatList) => {
  // Guid 优先，因为 Id 可能是 NULL→0 导致所有节点 id 相同
  const getGuid = (n) => n.Guid ?? n.guid ?? ''
  const getId = (n) => getGuid(n) || (n.Id ?? n.id)
  const getName = (n) => n.EnName ?? n.enName ?? n.name ?? ''
  const getParent = (n) => n.SuperiorGuid ?? n.superiorGuid ?? n.parentId ?? n.parentGuid ?? ''

  // 去重
  const seenGuid = new Set()
  const seenName = new Set()
  const dedup = []
  for (const n of flatList) {
    const g = getGuid(n)
    const sig = `${getName(n)}|${getParent(n)}`
    if (g && seenGuid.has(g)) continue
    if (seenName.has(sig)) continue
    seenGuid.add(g)
    seenName.add(sig)
    dedup.push(n)
  }

  const map = {}
  const roots = []
  for (const n of dedup) {
    const id = getId(n)
    if (id == null) continue
    map[id] = { id, guid: getGuid(n), name: getName(n), children: [] }
  }
  for (const n of dedup) {
    const id = getId(n)
    if (id == null) continue
    const node = map[id]
    const parentId = getParent(n)
    if (parentId && map[parentId]) {
      map[parentId].children.push(node)
    } else {
      roots.push(node)
    }
  }
  // 删除空的 children 标记
  const clean = (nodes) => {
    for (const n of nodes) {
      if (!n.children || n.children.length === 0) delete n.children
      else clean(n.children)
    }
  }
  clean(roots)
  return roots
}

/* ---------- 将目录树+报表平铺，用于渲染 ---------- */
const flatTree = computed(() => {
  const result = []

  const walk = (nodes, depth) => {
    for (const node of nodes) {
      const hasChildren = node.children && node.children.length > 0
      const key = 'cat_' + node.id
      result.push({
        ...node,
        _key: key,
        _depth: depth,
        _isReport: false,
        _reportCount: reportChildMap.value[node.id]?.length || 0
      })

      // 如果展开了，显示子目录 + 报表
      if (expandedFolderIds.value.includes(node.id)) {
        // 子目录
        if (hasChildren) {
          walk(node.children, depth + 1)
        }
        // 该目录下的报表
        const reports = reportChildMap.value[node.id]
        if (reports && reports.length > 0) {
          for (const r of reports) {
            result.push({
              ...r,
              _key: r._key,
              _depth: depth + 1,
              _isReport: true
            })
          }
        }
      }
    }
  }

  walk(reportFolderTree.value, 0)
  return result
})

/* ---------- 选中节点是报表还是目录 ---------- */
const selectedNodeIsReport = computed(() => {
  if (!selectedNodeKey.value) return false
  return selectedNodeKey.value.startsWith('report_')
})

/* ---------- 数据加载 ---------- */
const loadCatalogs = async () => {
  try {
    const res = await getCatalogs()
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    reportFolderTree.value = buildCatalogTree(list)
  } catch { reportFolderTree.value = [] }
}

/** 加载所有报表缓存（用于报表选择器） */
const loadAllReports = async () => {
  try {
    const res = await getAllReports()
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    allReportsCache.value = list.map(r => ({
      id: r.guid || r.Guid || r.id,
      guid: r.guid || r.Guid || r.id,
      name: r.chName || r.ChName || r.enName || r.EnName || '',
      folderId: r.catalogGuid || r.CatalogGuid || '',
      chartType: '报表'
    }))
  } catch { allReportsCache.value = [] }
}

/** 为目录节点加载子报表，添加到树中 */
const loadReportTreeChildren = async (folderNode) => {
  if (reportChildMap.value[folderNode.id]) return  // 已加载过
  try {
    const res = await getReports(folderNode.id)
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    const reportNodes = list.map(r => ({
      _key: 'report_' + (r.guid || r.Guid || r.id),
      id: r.guid || r.Guid || r.id,
      guid: r.guid || r.Guid || r.id,
      name: r.chName || r.ChName || r.enName || r.EnName || '',
      chartType: '报表',
      parentFolderId: folderNode.id
    }))
    reportChildMap.value[folderNode.id] = reportNodes
  } catch {
    reportChildMap.value[folderNode.id] = []
  }
}

/** 加载目录下所有报表的权限 */
const loadReportsAndPermissions = async (folderId) => {
  try {
    const res = await getReports(folderId)
    const reportList = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    const allPerms = []
    let idSeq = 1
    for (const rpt of reportList) {
      try {
        const permRes = await getReportPermissions(rpt.guid || rpt.Guid || rpt.id)
        const permList = Array.isArray(permRes) ? permRes : (permRes?.data || [])
        for (const item of permList) {
          const opType = (item.operatorType || '').toLowerCase()
          const opID = item.operatorID || ''
          let targetName = item.operatorPara1 || opID || ''
          if (opType === 'user') {
            const u = users.value.find(u => u.id === opID)
            if (u) targetName = u.name
          } else if (opType === 'org') {
            const o = orgs.value.find(o => o.id === opID)
            if (o) targetName = o.name
          } else if (opType === 'role') {
            const r = rls.value.find(r => r.id === opID)
            if (r) targetName = r.name
          }
          allPerms.push({
            id: idSeq++,
            guid: item.guid,
            reportId: rpt.guid || rpt.Guid || rpt.id,
            reportName: rpt.chName || rpt.ChName || rpt.enName || rpt.EnName || '',
            chartType: '报表',
            folderId: folderId,
            targetType: opType || 'user',
            targetId: opID,
            targetName: targetName,
            scope: alloyToScope(item.alloyPermission),
            status: item.active === 1 ? '启用' : '禁用',
            remark: item.remark || ''
          })
        }
      } catch { /* skip failed report */ }
    }
    permissions.value = allPerms
  } catch {
    permissions.value = []
  }
}

/** 加载单个报表的权限 */
const loadSingleReportPermissions = async (reportGuid, reportName) => {
  try {
    const permRes = await getReportPermissions(reportGuid)
    const permList = Array.isArray(permRes) ? permRes : (permRes?.data || [])
    const allPerms = []
    let idSeq = 1
    for (const item of permList) {
      const opType = (item.operatorType || '').toLowerCase()
      const opID = item.operatorID || ''
      let targetName = item.operatorPara1 || opID || ''
      if (opType === 'user') {
        const u = users.value.find(u => u.id === opID)
        if (u) targetName = u.name
      } else if (opType === 'org') {
        const o = orgs.value.find(o => o.id === opID)
        if (o) targetName = o.name
      } else if (opType === 'role') {
        const r = rls.value.find(r => r.id === opID)
        if (r) targetName = r.name
      }
      allPerms.push({
        id: idSeq++,
        guid: item.guid,
        reportId: reportGuid,
        reportName: reportName,
        chartType: '报表',
        targetType: opType || 'user',
        targetId: opID,
        targetName: targetName,
        scope: alloyToScope(item.alloyPermission),
        status: item.active === 1 ? '启用' : '禁用',
        remark: item.remark || ''
      })
    }
    permissions.value = allPerms
  } catch {
    permissions.value = []
  }
}

const loadUsers = async () => {
  try {
    const res = await getUserList()
    const list = Array.isArray(res) ? res : (res?.data || [])
    users.value = list.map(u => ({ id: u.guid, guid: u.guid, name: u.displayName || u.account || '' }))
  } catch { users.value = [] }
}

const loadOrgs = async () => {
  try {
    const res = await getOrgTree()
    const list = Array.isArray(res) ? res : (res?.data || [])
    orgTree.value = list
    const flat = (nodes) => {
      let r = []
      for (const n of (nodes || [])) {
        const name = n.ousName || n.oUsName || n.OUsName || ''
        const children = n.children || n.childFolder
        r.push({ id: n.guid, guid: n.guid, name })
        if (children) r = r.concat(flat(children))
      }
      return r
    }
    orgs.value = flat(list)
  } catch { orgTree.value = []; orgs.value = [] }
}

const loadRoles = async () => {
  try {
    const res = await getRoleTree()
    const list = Array.isArray(res) ? res : (res?.data || [])
    roleTree.value = list
    const flat = (nodes) => {
      let r = []
      for (const n of (nodes || [])) {
        const children = n.children || n.childFolder
        r.push({ id: n.guid, guid: n.guid, name: n.roleName || '' })
        if (children) r = r.concat(flat(children))
      }
      return r
    }
    rls.value = flat(list)
  } catch { roleTree.value = []; rls.value = [] }
}

onMounted(() => { loadCatalogs(); loadUsers(); loadOrgs(); loadRoles(); loadAllReports() })

/* ---------- 计算属性 ---------- */
const filteredData = computed(() => {
  let r = permissions.value
  if (searchForm.reportName) r = r.filter(p => p.reportName.includes(searchForm.reportName))
  if (searchForm.targetType) r = r.filter(p => p.targetType === searchForm.targetType)
  return r
})
const totalCount = computed(() => filteredData.value.length)
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value) || 1)
const paginatedData = computed(() => { const s = (currentPage.value - 1) * pageSize.value; return filteredData.value.slice(s, s + pageSize.value) })
const visiblePages = computed(() => { const ps = [], t = totalPages.value, c = currentPage.value; if (t <= 7) { for (let i = 1; i <= t; i++) ps.push(i) } else { ps.push(1); if (c > 4) ps.push('...'); for (let i = Math.max(2, c - 2); i <= Math.min(t - 1, c + 2); i++) ps.push(i); if (c < t - 3) ps.push('...'); ps.push(t) } return ps })
const filteredReports = computed(() => !reportSearch.value ? allReportsCache.value : allReportsCache.value.filter(r => r.name.includes(reportSearch.value)))
const targetTypeLabel = computed(() => ({ user: '用户', org: '组织', role: '角色' })[form.targetType] || '')
function flattenForPicker(nodes, level = 0) {
  let result = []
  for (const n of (nodes || [])) {
    const name = form.targetType === 'org'
      ? (n.ousName || n.oUsName || n.OUsName || '')
      : (n.roleName || '')
    const children = n.children || n.childFolder || []
    result.push({ id: n.guid, name, level, hasChildren: children.length > 0 })
    if (children.length > 0) {
      result = result.concat(flattenForPicker(children, level + 1))
    }
  }
  return result
}
const filteredTargets = computed(() => {
  if (form.targetType === 'user') {
    return !targetSearch.value ? users.value : users.value.filter(i => (i.name || '').includes(targetSearch.value))
  }
  const tree = form.targetType === 'org' ? orgTree.value : roleTree.value
  let flat = flattenForPicker(tree)
  if (targetSearch.value) flat = flat.filter(i => (i.name || '').includes(targetSearch.value))
  return flat
})

/* ---------- 目录树操作 ---------- */
const toggleExpandFolder = async (node) => {
  const idx = expandedFolderIds.value.indexOf(node.id)
  if (idx !== -1) {
    expandedFolderIds.value.splice(idx, 1)
  } else {
    expandedFolderIds.value.push(node.id)
    // 展开时加载子报表
    await loadReportTreeChildren(node)
  }
}

const selectTreeNode = async (node) => {
  selectedNodeKey.value = node._key
  currentPage.value = 1

  if (node._isReport) {
    // 点击报表节点 → 只加载该报表权限
    await loadSingleReportPermissions(node.guid, node.name)
  } else {
    // 点击目录节点 → 展开并加载该目录下所有报表权限
    if (!expandedFolderIds.value.includes(node.id)) {
      expandedFolderIds.value.push(node.id)
      await loadReportTreeChildren(node)
    }
    await loadReportsAndPermissions(node.id)
  }
}

/* ---------- 搜索/分页 ---------- */
const handleSearch = () => { currentPage.value = 1 }
const resetSearch = () => { Object.keys(searchForm).forEach(k => searchForm[k] = ''); currentPage.value = 1 }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }

/* ---------- 表单操作 ---------- */
const openAddDialog = () => {
  editingId.value = null
  let folderId = null
  // 从选中的节点推断目录 ID
  if (selectedNodeKey.value) {
    if (selectedNodeKey.value.startsWith('report_')) {
      const reports = Object.values(reportChildMap.value).flat()
      const r = reports.find(r => r._key === selectedNodeKey.value)
      folderId = r?.parentFolderId || null
    } else {
      folderId = selectedNodeKey.value.replace('cat_', '')
    }
  }
  Object.assign(form, { id: null, guid: null, reportId: null, reportName: '', folderId, targetType: 'user', targetId: null, targetName: '', scope: 'view', status: '启用', remark: '' })
  showFormModal.value = true
}
const editPermission = (p) => { editingId.value = p.id; Object.assign(form, { ...p }); showFormModal.value = true }
const closeFormModal = () => { showFormModal.value = false; editingId.value = null }

const saveForm = async () => {
  if (!form.reportId || !form.targetId) { window.$message?.warning?.('请选择报表和授权对象'); return }
  const payload = {
    Guid: form.guid || undefined,
    OperatorType: form.targetType === 'user' ? 'User' : form.targetType === 'org' ? 'Org' : 'Role',
    OperatorID: form.targetId,
    OperatorPara1: form.targetName,
    ResourceType: 'Report',
    ResourceID: form.reportId,
    AlloyPermission: buildAlloy(form.scope, form.status),
    Active: form.status === '启用' ? 1 : 0,
    Remark: form.remark
  }
  try {
    await saveReportPermission(payload)
    closeFormModal()
    await refreshCurrentPermissions()
  } catch (e) { console.error('保存权限失败', e) }
}

const toggleStatus = async (p) => {
  const newStatus = p.status === '启用' ? '禁用' : '启用'
  try {
    await saveReportPermission({
      Guid: p.guid,
      OperatorType: p.targetType === 'user' ? 'User' : p.targetType === 'org' ? 'Org' : 'Role',
      OperatorID: p.targetId,
      OperatorPara1: p.targetName,
      ResourceType: 'Report',
      ResourceID: p.reportId,
      AlloyPermission: buildAlloy(p.scope, newStatus),
      Active: newStatus === '启用' ? 1 : 0
    })
    p.status = newStatus
  } catch (e) { console.error('切换状态失败', e) }
}

const deletePermission = async (p) => {
  if (!confirm('确定删除该权限记录吗？')) return
  try {
    await deleteReportPermission(p.guid)
    permissions.value = permissions.value.filter(x => x.id !== p.id)
  } catch (e) { console.error('删除权限失败', e) }
}

/** 刷新当前选中节点的权限数据 */
const refreshCurrentPermissions = async () => {
  if (!selectedNodeKey.value) return
  if (selectedNodeKey.value.startsWith('report_')) {
    const reports = Object.values(reportChildMap.value).flat()
    const r = reports.find(r => r._key === selectedNodeKey.value)
    if (r) await loadSingleReportPermissions(r.guid, r.name)
  } else {
    const catId = selectedNodeKey.value.replace('cat_', '')
    await loadReportsAndPermissions(catId)
  }
}

/* ---------- 选择器 ---------- */
const selectReport = (r) => { form.reportId = r.id; form.reportName = r.name; form.folderId = r.folderId; showReportPicker.value = false }
const selectTarget = (item) => { form.targetId = item.id; form.targetName = item.name; showTargetPicker.value = false }
const openTargetPicker = () => { targetSearch.value = ''; showTargetPicker.value = true }
</script>

<style scoped>
.view-container { padding: 16px; background: #f5f7fa; height: 100%; }
.main-layout { display: flex; gap: 16px; height: calc(100vh - 140px); min-height: 0; }
.left-panel { flex: 0 0 22%; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
.panel-header { padding: 10px 12px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; flex-shrink: 0; }
.panel-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #374151; }
.panel-body { height: 100%; padding: 8px; overflow-y: auto; }
.right-panel { flex: 1; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; }
.tree-table { width: 100%; border-collapse: collapse; }
.tree-table thead th { padding: 8px 10px; font-size: 11px; font-weight: 600; color: #6b7280; background: #f8fafc; border-bottom: 1px solid #e8ecf1; text-align: left; }
.tree-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; color: #374151; }
.tree-row { cursor: pointer; transition: background 0.1s; }
.tree-row:hover { background: #f9fafb; }
.tree-row.selected { background: #ecfdf5; }
.tree-row.selected td { color: #059669; }
.tree-row.is-report.selected { background: #eef2ff; }
.tree-row.is-report.selected td { color: #4f46e5; }
.tree-name-cell { display: flex; align-items: center; gap: 4px; }
.expand-icon { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 9px; cursor: pointer; transition: transform 0.15s; }
.expand-icon:hover { color: #10b981; }
.expand-icon.expanded i { transform: rotate(90deg); }
.expand-placeholder { width: 14px; }
.tree-name-cell i { color: #10b981; font-size: 11px; }
.tree-name { margin-left: 2px; font-size: 12px; }
.count-badge { margin-left: 4px; background: #ecfdf5; color: #059669; font-size: 10px; padding: 0 5px; border-radius: 8px; min-width: 16px; text-align: center; display: inline-block; line-height: 16px; }

.search-panel { padding: 10px 14px; background: #fafbfc; border-bottom: 1px solid #e8ecf1; }
.search-row { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.search-item { display: flex; align-items: center; gap: 6px; }
.search-item label { font-size: 13px; color: #374151; font-weight: 500; white-space: nowrap; }
.search-item label i { color: #10b981; margin-right: 2px; font-size: 12px; }
.search-input, .search-select { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 160px; outline: none; transition: border-color 0.2s; }
.search-input:focus, .search-select:focus { border-color: #10b981; }
.search-actions { display: flex; gap: 6px; margin-left: auto; align-items: center; }
.search-btn { padding: 6px 12px; font-size: 12px; border-radius: 6px; display: flex; align-items: center; gap: 4px; }

.table-container-inner { flex: 1; overflow-y: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 8px 10px; border-bottom: 1px solid #e7eaec; font-size: 12px; color: #676a6c; text-align: left; white-space: nowrap; }
.data-table th { background: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }
.empty-cell { text-align: center; padding: 40px; color: #999; }
.action-cell { white-space: nowrap; text-align: center; }
.action-cell .btn-xs { margin: 0 2px; padding: 4px 8px; font-size: 11px; border-radius: 4px; cursor: pointer; border: 1px solid transparent; }
.btn-primary.btn-xs { background: #3498db; color: #fff; }
.btn-warning.btn-xs { background: #f39c12; color: #fff; }
.btn-danger.btn-xs { background: #e74c3c; color: #fff; }
.btn-success.btn-xs { background: #27ae60; color: #fff; }
.type-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; background: #f0f4ff; color: #6366f1; }
.chart-type-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: 500; background: #ede9fe; color: #7c3aed; }
.scope-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 500; background: #fef3c7; color: #d97706; }
.status-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500; }
.status-badge.enabled { background: #dcfce7; color: #16a34a; }
.status-badge.disabled { background: #fee2e2; color: #dc2626; }

.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 12px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 8px; }
.page-numbers { display: flex; gap: 4px; }
.page-btn { width: 28px; height: 28px; border: 1px solid #e7eaec; background: #fff; color: #676a6c; border-radius: 3px; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; }
.page-btn:hover:not(.disabled) { border-color: #10b981; color: #10b981; }
.page-btn.active { background: #10b981; border-color: #10b981; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 28px; height: 28px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 4px; font-size: 12px; }
.page-size-input { width: 60px; padding: 3px 6px; font-size: 12px; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; color: #9ca3af; }
.empty-state i { font-size: 48px; margin-bottom: 12px; color: #d1d5db; }
.empty-state p { font-size: 14px; margin-bottom: 4px; }
.empty-state .hint { font-size: 12px; color: #d1d5db; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; width: 600px; max-height: 80vh; overflow-y: auto; }
.picker-modal { width: 450px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h3 { margin: 0; font-size: 16px; }
.close-btn { background: none; border: none; font-size: 22px; cursor: pointer; color: #999; }
.close-btn:hover { color: #333; }
.modal-body { padding: 20px; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.form-row { display: flex; gap: 15px; margin-bottom: 15px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 13px; color: #676a6c; margin-bottom: 5px; font-weight: 500; }
.form-group .form-control { width: 100%; padding: 8px 12px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-group textarea.form-control { resize: vertical; }
.search-select { display: flex; gap: 6px; }
.search-select .form-control { flex: 1; cursor: pointer; }
.search-box { margin-bottom: 12px; }
.search-input-wrap { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 10px; color: #9ca3af; font-size: 13px; z-index: 1; }
.search-box-input { width: 100%; padding: 8px 10px 8px 30px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; box-sizing: border-box; transition: border-color 0.2s; }
.search-box-input:focus { border-color: #10b981; }
.picker-list { max-height: 320px; overflow-y: auto; }
.picker-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; font-size: 13px; color: #374151; transition: background 0.15s; }
.picker-item:hover { background: #f9fafb; }
.picker-item.selected { background: #ecfdf5; color: #059669; font-weight: 500; }
.picker-item i { color: #10b981; font-size: 12px; width: 16px; text-align: center; flex-shrink: 0; }
.tree-level-0 { padding-left: 14px; }
.tree-level-1 { padding-left: 36px; }
.tree-level-2 { padding-left: 58px; }
.tree-level-3 { padding-left: 80px; }
.tree-hint { margin-left: auto; font-size: 10px; color: #d1d5db; }
.picker-empty { text-align: center; padding: 30px 0; color: #9ca3af; font-size: 13px; }
.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-primary { background: #10b981; color: #fff; border-color: #10b981; }
.btn-primary:hover { background: #059669; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-success { background: #6366f1; color: #fff; border-color: #6366f1; }
.btn-success:hover { background: #4f46e5; }
.btn-warning { background: #f59e0b; color: #fff; border-color: #f59e0b; }
.btn-warning:hover { background: #d97706; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
.report-row { background: #fafbfc; }
</style>
